import React from "react";
import { Link } from "react-router-dom";
import ServiceMark from "./ServiceMark";
import { buildHeroFactsLine, buildHeroTypeGenreLine, formatGenreLabelsForDisplay } from "../appleTvPosterMeta";

/**
 * Shared trailer hero details dock — identical on home carousel and movie detail.
 *
 * Layout:
 *   Left:  highlight tag → title → [icon] type · genres · cert
 *          → year · runtime · ★ score (count)
 *   Right: director + starring
 *
 * Taglines are intentionally not shown on the home carousel or movie detail hero.
 */
export default function HeroTrailerDetails({
  highlightTag = null,
  title = "",
  serviceBadge = null,
  mediaType = "movie",
  genres = [],
  genre = "",
  certification = null,
  year = null,
  yearLabel = null,
  runtime = null,
  score = null,
  ratingsCount = 0,
  tmdbScore = null,
  fromTmdbOnly = false,
  scoreAsRaw = false,
  // Accepted for API compatibility; taglines are not rendered in the hero.
  tagline: _unusedTagline = "",
  taglineExtra = null,
  directors = [],
  castTop3 = [],
  overlayClassName = "",
  featuredCard = false,
  hideMediaType = false,
  combineServiceWithFacts = false,
  /** Home carousel dock: ★ score · Genre1 & Genre2 · streaming icon. */
  carouselDockLine = false,
  /** Random shelf: one line — ★ score · Title · streaming icon. */
  shelfInlineLine = false,
  /** When set with shelfInlineLine, title + chevron link to the detail page. */
  titleTo = null,
}) {
  void _unusedTagline;
  const genreList = Array.isArray(genres) && genres.length
    ? formatGenreLabelsForDisplay(genres, { max: 2 })
    : formatGenreLabelsForDisplay(genre ? [genre] : [], { max: 1 });
  const genresJoined = genreList.length
    ? genreList.join(" & ")
    : "";
  const scoreValue = Number(score);
  const scoreLabel = Number.isFinite(scoreValue) && scoreValue > 0 && scoreValue <= 100
    ? (scoreAsRaw ? `★ ${Math.round(scoreValue)}` : `★ ${Math.round(scoreValue)}%`)
    : null;
  const typeGenreLine = buildHeroTypeGenreLine({
    mediaType,
    genres,
    genre,
    certification,
    includeMediaType: !hideMediaType,
  });
  const factsLine = buildHeroFactsLine({
    year,
    yearLabel,
    runtime,
    score,
    ratingsCount,
    tmdbScore,
    fromTmdbOnly,
    scoreAsRaw,
  });
  const combinedDetailsLine = combineServiceWithFacts
    ? [factsLine, typeGenreLine].filter(Boolean).join(" · ")
    : "";
  const directorNames = Array.isArray(directors) ? directors.filter(Boolean) : [];
  const castNames = Array.isArray(castTop3) ? castTop3.filter(Boolean) : [];
  const hasPeople = directorNames.length > 0 || castNames.length > 0;
  const showShelfInline = shelfInlineLine && (scoreLabel || title || serviceBadge);
  const showCarouselLine = !shelfInlineLine && carouselDockLine && (serviceBadge || scoreLabel || genresJoined);
  const showCombinedLine = !shelfInlineLine && !carouselDockLine && combineServiceWithFacts && (serviceBadge || combinedDetailsLine);
  const showMetaLine = !shelfInlineLine && !carouselDockLine && !combineServiceWithFacts && (serviceBadge || typeGenreLine);
  const showFactsLine = !shelfInlineLine && !carouselDockLine && !combineServiceWithFacts && factsLine;
  const hasServiceGutter = Boolean(serviceBadge) && !combineServiceWithFacts && !carouselDockLine && !shelfInlineLine;

  return (
    <div className={`apple-clone__detail-compact-overlay apple-clone__hero-compact-overlay${overlayClassName ? ` ${overlayClassName}` : ""}`}>
      <div className="apple-clone__hero-details-layout">
        <div className={`apple-clone__detail-compact-main${hasServiceGutter ? " has-service-gutter" : ""}`}>
          {highlightTag && !shelfInlineLine ? (
            <span className="apple-clone__detail-award">{highlightTag}</span>
          ) : null}
          {showShelfInline ? (
            <div className="apple-clone__detail-compact-line apple-clone__detail-compact-line--meta apple-clone__detail-compact-line--shelf-inline">
              {scoreLabel ? (
                <span className="apple-clone__detail-carousel-score">{scoreLabel}</span>
              ) : null}
              {title ? (
                titleTo ? (
                  <Link
                    to={titleTo}
                    className="apple-clone__detail-shelf-inline-title-link"
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    <span className="apple-clone__detail-shelf-inline-title">{title}</span>
                    <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
                    <span className="apple-clone__rail-chevron apple-clone__detail-shelf-inline-chevron" aria-hidden="true" />
                  </Link>
                ) : (
                  <>
                    <span className="apple-clone__detail-shelf-inline-title">{title}</span>
                    <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
                  </>
                )
              ) : (
                <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
              )}
            </div>
          ) : null}
          {!shelfInlineLine && title ? (
            <div className="apple-clone__detail-compact-title">
              <span className="apple-clone__title-glaze">{title}</span>
            </div>
          ) : null}
          {showCarouselLine ? (
            <div className="apple-clone__detail-compact-line apple-clone__detail-compact-line--meta apple-clone__detail-compact-line--carousel">
              {scoreLabel ? (
                <span className="apple-clone__detail-carousel-score">{scoreLabel}</span>
              ) : null}
              {genresJoined ? (
                <span className="apple-clone__detail-carousel-genres">{genresJoined}</span>
              ) : null}
              <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
            </div>
          ) : null}
          {showCombinedLine ? (
            <div className="apple-clone__detail-compact-line apple-clone__detail-compact-line--meta apple-clone__detail-compact-line--combined">
              <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
              {combinedDetailsLine ? <span>{combinedDetailsLine}</span> : null}
            </div>
          ) : null}
          {showMetaLine ? (
            <div className="apple-clone__detail-compact-line apple-clone__detail-compact-line--meta">
              <ServiceMark badge={serviceBadge} featuredCard={featuredCard} />
              {typeGenreLine ? <span>{typeGenreLine}</span> : null}
            </div>
          ) : null}
          {showFactsLine ? (
            <div className="apple-clone__detail-compact-line apple-clone__detail-compact-line--facts">
              {factsLine}
            </div>
          ) : null}
          {taglineExtra ? (
            <div className="apple-clone__detail-compact-tagline">
              {taglineExtra}
            </div>
          ) : null}
        </div>
        {hasPeople ? (
          <div className="apple-clone__detail-compact-people">
            {directorNames.length ? (
              <div className="apple-clone__detail-people-block">
                <div className="apple-clone__detail-people-label">Director</div>
                {directorNames.map((name) => (
                  <div key={`director-${name}`} className="apple-clone__detail-people-name">{name}</div>
                ))}
              </div>
            ) : null}
            {castNames.length ? (
              <div className="apple-clone__detail-people-block">
                <div className="apple-clone__detail-people-label">Starring</div>
                {castNames.map((name) => (
                  <div key={`cast-${name}`} className="apple-clone__detail-people-name">{name}</div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
